import { useEffect, useRef, useState } from 'react';
import { LEGACY_DRAFTS_STORAGE_KEYS, MIGRATE_ENDPOINTS_BY_ENTITY } from 'features/drafts';
import { useShowAlert } from 'hooks';
import { SECONDARY_API_CLIENT } from 'services/API';
import strings from 'strings';
import { loadFromLocalStorage } from 'UI/utils';

const EMPTY_MIGRATION_RESULT = {
  failedCount: 0,
  migratedCount: 0,
  skippedCount: 0
};

const getLegacyDraftsByEntity = entity => {
  const key = LEGACY_DRAFTS_STORAGE_KEYS[entity];
  if (!key) return [];

  const rawLegacyDrafts = loadFromLocalStorage(key, []);

  return Array.isArray(rawLegacyDrafts) ? rawLegacyDrafts : [];
};

const hasLegacyDrafts = entity => getLegacyDraftsByEntity(entity).length > 0;

export const removeLegacyDraftById = ({ draftId, entity }) => {
  try {
    const storageKey = LEGACY_DRAFTS_STORAGE_KEYS[entity];
    if (!storageKey) {
      return false;
    }

    const legacyDrafts = getLegacyDraftsByEntity(entity);
    const updatedDrafts = legacyDrafts.filter(draft => draft.id !== draftId);
    localStorage.setItem(storageKey, JSON.stringify(updatedDrafts));
    return true;
  } catch (error) {
    return false;
  }
};

export const migrateLegacyDraftsByEntity = async entity => {
  const migrationEndpoint = MIGRATE_ENDPOINTS_BY_ENTITY[entity];
  if (!migrationEndpoint) {
    return EMPTY_MIGRATION_RESULT;
  }

  const legacyDrafts = getLegacyDraftsByEntity(entity);

  if (legacyDrafts.length === 0) {
    return EMPTY_MIGRATION_RESULT;
  }

  const response = await SECONDARY_API_CLIENT.put(migrationEndpoint, legacyDrafts);

  return {
    failedCount: response?.data?.error_count ?? 0,
    migratedCount: (response?.data?.created ?? 0) + (response?.data?.updated ?? 0),
    skippedCount: response?.data?.skipped ?? 0
  };
};

export const useLegacyDraftMigration = ({ entity }) => {
  const { showAlert } = useShowAlert();
  const isMountedRef = useRef(true);
  const [isMigratingLegacyDrafts, setIsMigratingLegacyDrafts] = useState(() =>
    hasLegacyDrafts(entity)
  );

  useEffect(() => {
    isMountedRef.current = true;

    const migrateDrafts = async () => {
      const legacyDrafts = getLegacyDraftsByEntity(entity);

      if (legacyDrafts.length === 0) {
        if (isMountedRef.current) {
          setIsMigratingLegacyDrafts(false);
        }
        return;
      }

      if (isMountedRef.current) {
        setIsMigratingLegacyDrafts(true);
      }

      try {
        const { failedCount } = await migrateLegacyDraftsByEntity(entity);

        if (!isMountedRef.current) return;

        if (failedCount > 0) {
          showAlert({
            title: strings.drafts.alert.error.migrateDraftsTitle,
            body: strings.formatString(strings.drafts.alert.error.migrateDrafts, {
              count: failedCount,
              plural: failedCount > 1 ? 's' : ''
            }),
            severity: 'error'
          });
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        showAlert({
          title: strings.drafts.alert.error.migrateDraftsTitle,
          error
        });
      } finally {
        if (isMountedRef.current) {
          setIsMigratingLegacyDrafts(false);
        }
      }
    };

    migrateDrafts();

    return () => {
      isMountedRef.current = false;
    };
  }, [showAlert, entity]);

  return {
    isMigratingLegacyDrafts
  };
};
